import { Component, Input, SimpleChanges, OnChanges } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import type { CollectionViewer } from '@angular/cdk/collections';
import { Store } from '@ngrx/store';
import { ProjectEntityListActions } from '../../store/project-entity-list/project-entity-list.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog/confirm-delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { selectSessionLocale } from 'src/app/auth/store/session/session.selectors';
import type { Entity } from '../../../core/models/entity.model';

@Component({
  selector: 'app-project-list-table',
  templateUrl: './project-list-table.component.html',
  styleUrls: ['./project-list-table.component.scss'],
  standalone: false
})
export class ProjectListTableComponent implements OnChanges {
  @Input() entities: Entity[] | null;

  uiLocale$: Observable<string>;

  displayedColumns = [
    'entityName',
    'entityProjectNumber',
    'entityCountry',
    'entityDateModified',
    'entityDateCreated',
    'entityCopyAction',
    'entityDeleteAction',
    'entityEditAction'
  ];

  constructor(private store: Store, private router: Router, private dialog: MatDialog, private route: ActivatedRoute) {
    // Info: for usage in Angular pipes: (uiLocale$ | async)
    this.uiLocale$ = this.store.select(selectSessionLocale);
  }

  dataSource = new EntityDataSource();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entities']) {
      this.dataSource.updateData(changes['entities'].currentValue ?? []);
    }
  }

  onRowClick(entity: Entity): void {
    this.router.navigate([entity.Id], { relativeTo: this.route });
  }

  onCopyClick(entity: Entity): void {
    this.store.dispatch(ProjectEntityListActions.copy_entity_start({ id: entity.Id }));
  }

  onDeleteClick(entity: Entity): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, { data: entity });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(ProjectEntityListActions.delete_entity_start({ entityIdList: [entity.Id] }));
      }
    });
  }

  onEditClick(entity: Entity): void {
    this.router.navigate([entity.Id], { relativeTo: this.route });
  }

  getCountryValue(entity: Entity): string {
    const countryCode = entity.Properties['CountryCode'].Value;
    return `Country.${countryCode}`;
  }
}

export class EntityDataSource implements DataSource<Entity> {
  private entitiesSubject = new BehaviorSubject<Entity[]>([]);

  connect(collectionViewer: CollectionViewer): Observable<Entity[] | readonly Entity[]> {
    return this.entitiesSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.entitiesSubject.complete();
  }

  updateData(entities: Entity[]): void {
    this.entitiesSubject.next(entities);
  }
}
